import React, { type ReactNode } from "react";
import { Text } from "@chakra-ui/react";
import { ProductFeaturesHero, type ProductFeaturesHeroProps } from "components/hero";
import { type ProductFeature } from "ui/product-features";
import { LottieWithStaticSafariImage } from "ui/lottie";
import { remCalc } from "theme/utils";
import heroImage from "../../../../../public/assets/platform/reverse-etl/hero.png";
import firstMobileImage from "../../../../../public/assets/platform/reverse-etl/mobile-hero-1.png";
import secondMobileImage from "../../../../../public/assets/platform/reverse-etl/mobile-hero-2.png";
import thirdMobileImage from "../../../../../public/assets/platform/reverse-etl/mobile-hero-3.png";
import { sourcesCount, integrationsCount } from "../../../../constants/integrations";
import animationData from "./animation.json";

function StyledTitle({ children }: { children: ReactNode }) {
  return (
    <Text as="span" lineHeight="28">
      {children}
    </Text>
  );
}

function InlineDescription({ children }: { children: ReactNode }) {
  return (
    <Text as="span" fontWeight="normal">
      {children}
    </Text>
  );
}

const features: ProductFeature[] = [
  {
    id: "retl-hero-feature-1",
    title: (
      <StyledTitle>
        Connect to any data warehouse, lake, or other source.{" "}
        <InlineDescription>Define the records to sync by selecting a table or writing SQL.</InlineDescription>
      </StyledTitle>
    ),
    mobileImage: {
      src: firstMobileImage,
      alt: "Three audience groups for Remarketing, Concert Attendees, and Newsletter Subscribers.",
    },
  },
  {
    id: "retl-hero-feature-2",
    title: (
      <StyledTitle>
        Map fields between your source and destination.{" "}
        <InlineDescription>Configure syncs in the app or take control via API.</InlineDescription>
      </StyledTitle>
    ),
    mobileImage: {
      src: secondMobileImage,
      alt: "Match booster enabled, expanding identifiers to include a user's personal email, phone number, work email, mobile IDs, and address data.",
    },
  },
  {
    id: "retl-hero-feature-3",
    title: (
      <StyledTitle>
        Sync to {integrationsCount}+ destinations in real-time or on a recurring schedule.{" "}
        <InlineDescription>Spin up new data pipelines in minutes — not weeks.</InlineDescription>
      </StyledTitle>
    ),
    mobileImage: {
      src: thirdMobileImage,
      alt: "Increased match rate by 35%, 48%, and 24% on TikTok, Facebook, and LinkedIn respectively.",
    },
  },
];

export function ReverseEtlHero({
  eyebrow,
  title,
  description,
}: Pick<ProductFeaturesHeroProps, "eyebrow" | "title" | "description">) {
  return (
    <ProductFeaturesHero
      includeSignupAction
      theme="dark"
      variant="green"
      spotlightColor="#2A6368"
      eyebrow={eyebrow}
      title={title}
      description={description}
      titleMaxW="640px"
      productFeatures={features}
      featureGridProps={{ mb: "big" }}
      mobileFeatureTextMinH={{ base: remCalc(112), sm: remCalc(84), md: "none" }}
      media={
        <LottieWithStaticSafariImage
          isPriority
          image={heroImage}
          imagePlaceholder="blur"
          alt={`Abstract illustration showing data flowing from ${sourcesCount}+ sources, into Hightouch where fields are mapped between the source and destination. Finally the data flows out of Hightouch into ${integrationsCount}+ destinations.`}
          animationData={animationData}
          ratio={1320 / 522}
          containerProps={{ borderRadius: "bigger", overflow: "hidden" }}
        />
      }
    />
  );
}
